/**
 * AvatarPicker Component
 * Grid layout for selecting avatars with haptic feedback and visual feedback
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { View, Platform } from 'react-native';
import type { ViewStyle } from 'react-native';
import * as Haptics from 'expo-haptics';
import type { AvatarId } from '../../../constants/avatarIds';
import { AVATAR_IDS } from '../../../constants/avatarIds';
import { AvatarItem } from './AvatarItem';
import { avatarPickerStyles as styles } from './AvatarPicker.styles';

export interface AvatarPickerProps {
  /** Currently selected avatar ID */
  selectedAvatarId?: AvatarId;
  /** Callback when avatar is selected */
  onAvatarSelect: (avatarId: AvatarId) => void;
  /** Optional custom styling */
  style?: ViewStyle;
  /** Test ID for testing */
  testID?: string;
}

/**
 * AvatarPicker Component
 * Displays a 2-column grid of avatars for selection
 * Provides haptic feedback and visual feedback (mood='happy', scale transform) when selected
 * Optimized with React.memo for smooth scrolling performance
 */
const AvatarPickerComponent: React.FC<AvatarPickerProps> = ({
  selectedAvatarId,
  onAvatarSelect,
  style,
  testID,
}) => {
  const [pressedAvatarId, setPressedAvatarId] = useState<AvatarId | null>(null);
  const [sparkAvatarId, setSparkAvatarId] = useState<AvatarId | null>(null);
  const previousSelectedRef = useRef<AvatarId | undefined>(selectedAvatarId);
  const sparkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePressIn = useCallback((avatarId: AvatarId) => {
    setPressedAvatarId(avatarId);
  }, []);

  const handlePressOut = useCallback(() => {
    setPressedAvatarId(null);
  }, []);

  // Clear any existing spark timeout
  const clearSparkTimeout = useCallback(() => {
    if (sparkTimeoutRef.current) {
      clearTimeout(sparkTimeoutRef.current);
      sparkTimeoutRef.current = null;
    }
  }, []);

  // Trigger spark effect with consistent timeout management
  const triggerSpark = useCallback((avatarId: AvatarId) => {
    clearSparkTimeout();
    setSparkAvatarId(avatarId);
    sparkTimeoutRef.current = setTimeout(() => {
      setSparkAvatarId(null);
      sparkTimeoutRef.current = null;
    }, 600);
  }, [clearSparkTimeout]);

  // Trigger spark effect when avatar is selected (from prop change)
  useEffect(() => {
    if (selectedAvatarId && selectedAvatarId !== previousSelectedRef.current) {
      triggerSpark(selectedAvatarId);
      previousSelectedRef.current = selectedAvatarId;
    }
    return () => {
      clearSparkTimeout();
    };
  }, [selectedAvatarId, triggerSpark, clearSparkTimeout]);

  const handlePress = useCallback(
    (avatarId: AvatarId) => {
      /**
       * Trigger haptic feedback when avatar is selected
       * 
       * Haptics.impactAsync provides tactile feedback on supported devices.
       * - ImpactFeedbackStyle.Light: A subtle, light haptic pulse
       * - Platform behavior:
       *   - Native (iOS/Android): Provides actual haptic feedback on device
       *   - Web: May be a no-op or throw an error if library isn't polyfilled correctly
       * 
       * Wrapped in try/catch to gracefully handle:
       * - Web platform where haptics may not be supported
       * - Devices that don't support haptic feedback
       * - Library initialization errors
       */
      if (Platform.OS !== 'web') {
        try {
          Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
        } catch {
          // Silently fail on unsupported devices
        }
      }

      // Always trigger spark effect on click (consistent behavior)
      triggerSpark(avatarId);

      // Call the selection callback
      onAvatarSelect(avatarId);
    },
    [onAvatarSelect, triggerSpark]
  );

  // Only user-selectable avatars (AVATAR_IDS; monkey is not in this list)
  const avatarIds = useMemo(() => Object.values(AVATAR_IDS) as AvatarId[], []);

  return (
    <View style={[styles.container, style]} testID={testID || 'avatar-picker'}>
      {avatarIds.map((avatarId) => (
        <AvatarItem
          key={avatarId}
          avatarId={avatarId}
          isSelected={selectedAvatarId === avatarId}
          isPressed={pressedAvatarId === avatarId}
          showSpark={sparkAvatarId === avatarId}
          onPressIn={handlePressIn}
          onPressOut={handlePressOut}
          onPress={handlePress}
        />
      ))}
    </View>
  );
};

/**
 * Memoized AvatarPicker Component
 * Prevents re-renders when parent re-renders unless props change
 */
export const AvatarPicker = React.memo(AvatarPickerComponent, (prevProps, nextProps) => {
  // Only re-render if these props change
  return (
    prevProps.selectedAvatarId === nextProps.selectedAvatarId &&
    prevProps.onAvatarSelect === nextProps.onAvatarSelect &&
    prevProps.style === nextProps.style &&
    prevProps.testID === nextProps.testID
  );
});

AvatarPicker.displayName = 'AvatarPicker';

